import { notifyError } from '@/common/utils/notifyError';
import { contentTypeHeaders, handleResponse } from '../handleResponse';
import {
  SendOTPResponse,
  VerificationResult,
  VerifyAdditionalValidationRequest,
  VerifyCarrierPinRequest,
  VerifyLastNamePostalCodeRequest,
} from '../types';
import {
  FinalizeResponse,
  InitRequest,
  ResolveIdentifierResponse,
  SignInResult,
} from './SignInTypes';

export class SignInApiBaseClient {
  private readonly signInEndpoint;

  constructor(url = '') {
    this.signInEndpoint = `${url}/v2/api`;
  }

  public async initializeAuthClient(initRequest: InitRequest): Promise<void> {
    const response = await fetch(`${this.signInEndpoint}/init-auth-client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        browserFingerprint: initRequest.browserFingerprint,
        browserTimezoneOffset: initRequest.browserTimezoneOffset,
      }),
    });
    const status = response.status;
    if (!response.ok) {
      const error = new Error(
        `Error ${status}: ${response?.statusText} - Failed to initialize Auth Client: ${response?.url}`,
      );
      notifyError(error);
    }
  }

  public async resolveIdentifier(
    identifier: string,
  ): Promise<ResolveIdentifierResponse> {
    const response = await fetch(`${this.signInEndpoint}/resolve-identifier`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify({ identifier }),
    });
    return await handleResponse<ResolveIdentifierResponse>(response);
  }

  public async sendOTP(sendByVoice?: boolean): Promise<SendOTPResponse> {
    const response = await fetch(`${this.signInEndpoint}/send-otp`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: sendByVoice ? JSON.stringify({ sendByVoice }) : undefined,
    });
    return await handleResponse<SendOTPResponse>(response);
  }

  public async verifyOTP(
    transactionId: string,
    value: string,
  ): Promise<VerificationResult> {
    const response = await fetch(`${this.signInEndpoint}/verify-otp`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify({ transactionId, value }),
    });
    return await handleResponse<VerificationResult>(response);
  }

  public async finalize(
    lexisNexisSessionId?: string,
  ): Promise<FinalizeResponse> {
    const response = await fetch(`${this.signInEndpoint}/finalize`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify({ lexisNexisSessionId }),
    });
    return await handleResponse<FinalizeResponse>(response);
  }

  public async updatePassword(password: string): Promise<SignInResult> {
    const response = await fetch(`${this.signInEndpoint}/update-password`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify({ password }),
    });
    return await handleResponse(response);
  }

  public async verifyPassword(password: string): Promise<VerificationResult> {
    const response = await fetch(`${this.signInEndpoint}/verify-password`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify({ password }),
    });
    return await handleResponse<VerificationResult>(response);
  }

  public async initiatePasswordReset(): Promise<void> {
    const response = await fetch(
      `${this.signInEndpoint}/initiate-password-reset`,
      {
        method: 'POST',
      },
    );
    return await handleResponse(response);
  }

  public async signInWithSocial(
    provider: string,
    idToken: string,
    nonce?: string,
  ): Promise<SignInResult> {
    const response = await fetch(`${this.signInEndpoint}/social/${provider}`, {
      method: 'POST',
      headers: {
        ...contentTypeHeaders,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: nonce
        ? JSON.stringify({ token: idToken, nonce: nonce })
        : JSON.stringify({ token: idToken }),
    });
    return await handleResponse<SignInResult>(response);
  }

  public async verifyLastNamePostalCode(
    request: VerifyAdditionalValidationRequest,
  ): Promise<VerificationResult> {
    const response = await fetch(
      `${this.signInEndpoint}/verify-last-name-postal-code`,
      {
        method: 'POST',
        headers: contentTypeHeaders,
        body: JSON.stringify(request),
      },
    );
    return await handleResponse<VerificationResult>(response);
  }

  public async verifyCarrierPin(
    request: VerifyCarrierPinRequest,
  ): Promise<VerificationResult> {
    const response = await fetch(`${this.signInEndpoint}/carrier-pin/verify`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify(request),
    });
    return await handleResponse(response);
  }

  public async verifySoftValidation(
    request: VerifyLastNamePostalCodeRequest,
  ): Promise<VerificationResult> {
    const response = await fetch(`${this.signInEndpoint}/soft/verify`, {
      method: 'POST',
      headers: contentTypeHeaders,
      body: JSON.stringify(request),
    });
    return await handleResponse(response);
  }
}

export const SignInApiClient = new SignInApiBaseClient();
